import { useMsal } from '@azure/msal-react';

import { roleTags } from '../auth/claims';
import { scopeConfidence } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { config } from '../env';
import { resetIngestPlannerStore } from '../surfaces/ingest/planner-store';

/**
 * The rail footer: who is signed in, what role claims they hold, and sign-out.
 *
 * Role claims are shown here as tags (the mock shows `CEQ-Legal` and `AIMS` in
 * this position). Scope is shown in the top bar next to the breadcrumb, also
 * per the mock — the two are separated on purpose, because they answer
 * different questions and users conflate them. The full readout of both lives
 * on the account card in TopBar's details panel.
 *
 * CLM-65 (D-65-2) — cloudEQ grants CLM roles through security groups, so a
 * member of CEQ-CLM-Admin carries no `roles` claim and the footer used to read
 * "No role claim" for the most privileged user. The tags now show app-role
 * claims first (unchanged) and then the roles the API will derive from the
 * configured `VITE_ENTRA_GROUP_CLM_*` group ids, de-duplicated — see
 * `roleTags` in claims.ts for the two honest fallbacks. Display only: nothing
 * here or anywhere in the SPA gates on a group-derived role (NFR-06).
 */
export function IdentityPanel() {
  const { instance } = useMsal();
  const { identity, isAuthenticated } = useIdentity();
  const confidence = scopeConfidence(identity, isAuthenticated);
  const roles = roleTags(identity, config.role_groups.length > 0);

  return (
    <div className="rfoot">
      <div className="who">
        <b>{identity.display_name ?? 'Unknown user'}</b>
        {identity.username ?? 'no UPN in token'}
      </div>

      <div className="fl">
        {roles.tags.map((role) => (
          <span className="tag ai" key={role}>
            {role}
          </span>
        ))}
        {/* Neither fallback is an error state and neither is styled as one. With
            no group id configured the SPA simply cannot name a role and says so;
            with ids configured and nothing matched, the user holds no CLM role
            group — a real NFR-05 group-hygiene condition the API will explain
            when it refuses. The brand system has no warning colour, so both stay
            neutral rather than inventing an amber. */}
        {roles.fallback === 'resolved_by_api' ? (
          <span className="tag out">Roles resolved by the API</span>
        ) : null}
        {roles.fallback === 'no_role_group' ? <span className="tag out">No role group</span> : null}
        {/* 'none' is no groups at all, not just no scope group: the API refuses
            every call for it, and a scope group alone would not fix that. */}
        {confidence === 'none' ? <span className="tag out">No groups in your sign-in</span> : null}
      </div>

      <button
        className="btn qt sm"
        type="button"
        onClick={() => {
          // D-67-6 — the Ingest planner keeps its plan in memory across surface
          // switches; a shared browser must never show it to the next user.
          resetIngestPlannerStore();
          void instance.logoutRedirect({
            postLogoutRedirectUri: config.post_logout_redirect_uri,
          });
        }}
      >
        Sign out
      </button>
    </div>
  );
}
